import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Users, Plus, Check, Video, Calendar, Clock } from "lucide-react";
import { useAuth } from "../lib/auth";
import { useLanguage } from "../i18n/LanguageContext";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const blankForm = () => ({
  name: "",
  description: "",
  schedule: "",
  zoom_url: "",
  zoom_passcode: "",
  recurrence: { days_of_week: ["Mon", "Wed", "Fri"], time: "18:00", tz: "Asia/Kuala_Lumpur" },
});

const fmtCountdown = (mins, t) => {
  if (mins === null || mins === undefined) return t("halaqa.scheduleTba");
  if (mins <= 0) return t("halaqa.liveNow");
  if (mins < 60) return t("halaqa.startsInMinutes", { mins });
  const h = Math.floor(mins / 60), m = mins % 60;
  if (h < 24) return t("halaqa.startsInHoursMinutes", { h, m });
  return t("halaqa.startsInDaysHours", { d: Math.floor(h / 24), h: h % 24 });
};

export default function Halaqa() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [classes, setClasses] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(blankForm());

  const load = () => api.get("/classes").then((r) => setClasses(r.data)).catch(() => toast.error(t("halaqa.couldnTLoadHalaqas")));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const join = async (id) => {
    try { await api.post("/classes/join", { class_id: id }); toast.success(t("halaqa.joinedTheHalaqa")); load(); }
    catch { toast.error(t("halaqa.couldnTJoin")); }
  };

  const attendAndOpen = async (c) => {
    try {
      await api.post("/classes/attend", { class_id: c.class_id });
      toast.success(t("halaqa.attendanceMarked"));
    } catch (e) {
      toast.info(e?.response?.data?.detail || t("halaqa.joiningZoom"));
    }
    if (c.zoom_url) window.open(c.zoom_url, "_blank", "noopener");
  };

  const toggleDay = (d) => {
    const days = form.recurrence.days_of_week.includes(d)
      ? form.recurrence.days_of_week.filter((x) => x !== d)
      : [...form.recurrence.days_of_week, d];
    setForm({ ...form, recurrence: { ...form.recurrence, days_of_week: days } });
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/classes", form);
      toast.success(t("halaqa.halaqaCreated"));
      setForm(blankForm()); setCreating(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("halaqa.invalidForm")); }
  };

  const canCreate = user?.role === "mentor" || user?.role === "admin";
  const canJoinSoon = (c) => c.minutes_until !== null && c.minutes_until <= 30;

  return (
    <div className="space-y-8" data-testid="halaqa-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-gold-600 font-semibold">{t("halaqa.halaqa")}</div>
          <h1 className="mt-1 font-display font-semibold text-4xl text-forest-700">{t("halaqa.classesCohorts")}</h1>
          <p className="mt-2 text-foreground/70 max-w-xl">{t("halaqa.joinATeacherLedHalaqa")}</p>
        </div>
        {canCreate && (
          <button onClick={() => setCreating(!creating)} className="btn-primary" data-testid="create-class-toggle">
            <Plus className="h-4 w-4" /> {creating ? t("halaqa.cancel") : t("halaqa.newHalaqa")}
          </button>
        )}
      </header>

      {creating && canCreate && (
        <form onSubmit={create} className="hifz-card space-y-4" data-testid="create-class-form">
          <div className="grid gap-3">
            <input required data-testid="class-name-input" placeholder={t("halaqa.halaqaName")}
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="rounded-xl border border-sand-300 bg-white px-4 py-3 text-sm" />
            <input data-testid="class-desc-input" placeholder={t("halaqa.shortDescription")}
              value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="rounded-xl border border-sand-300 bg-white px-4 py-3 text-sm" />
          </div>

          <div className="rounded-xl border border-sand-300 bg-sand-200/40 p-4 space-y-3">
            <div className="text-xs uppercase tracking-[0.18em] text-gold-600 font-semibold">{t("halaqa.recurringSchedule")}</div>
            <div className="flex flex-wrap gap-1.5" data-testid="day-picker">
              {DAYS.map((d) => (
                <button type="button" key={d} onClick={() => toggleDay(d)}
                  data-testid={`day-${d}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                    form.recurrence.days_of_week.includes(d) ? "bg-forest-600 text-sand-100 border-forest-600" : "bg-white border-sand-300 text-foreground/70"
                  }`}>{d}</button>
              ))}
            </div>
            <div className="flex flex-wrap gap-3 items-center">
              <label className="text-xs text-muted-foreground">{t("halaqa.time")}</label>
              <input type="time" data-testid="time-input"
                value={form.recurrence.time}
                onChange={(e) => setForm({ ...form, recurrence: { ...form.recurrence, time: e.target.value } })}
                className="rounded-xl border border-sand-300 bg-white px-3 py-2 text-sm" />
              <label className="text-xs text-muted-foreground">{t("halaqa.timezone")}</label>
              <input data-testid="tz-input" placeholder={t("halaqa.asiaKualaLumpur")}
                value={form.recurrence.tz}
                onChange={(e) => setForm({ ...form, recurrence: { ...form.recurrence, tz: e.target.value } })}
                className="rounded-xl border border-sand-300 bg-white px-3 py-2 text-sm flex-1 min-w-[180px]" />
            </div>
          </div>

          <div className="rounded-xl border border-sand-300 bg-sand-200/40 p-4 space-y-3">
            <div className="text-xs uppercase tracking-[0.18em] text-gold-600 font-semibold">{t("halaqa.zoomOptional")}</div>
            <div className="grid gap-3">
              <input data-testid="zoom-url-input" placeholder="https://zoom.us/j/123…"
                value={form.zoom_url} onChange={(e) => setForm({ ...form, zoom_url: e.target.value })}
                className="rounded-xl border border-sand-300 bg-white px-3 py-2 text-sm" />
              <input data-testid="zoom-passcode-input" placeholder={t("halaqa.passcode")}
                value={form.zoom_passcode} onChange={(e) => setForm({ ...form, zoom_passcode: e.target.value })}
                className="rounded-xl border border-sand-300 bg-white px-3 py-2 text-sm" />
            </div>
            <p className="text-[11px] text-muted-foreground">{t("halaqa.passcodeIsHiddenFromStudents")}</p>
          </div>

          <button type="submit" className="btn-accent w-full" data-testid="create-class-submit">{t("halaqa.createHalaqa")}</button>
        </form>
      )}

      <div className="grid gap-5">
        {classes.length === 0 && (
          <div className="hifz-card col-span-full text-center text-muted-foreground">{t("halaqa.noHalaqasYet")}</div>
        )}
        {classes.map((c) => (
          <div key={c.class_id} className="hifz-card hifz-card-hover" data-testid={`class-card-${c.class_id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="h-11 w-11 rounded-xl bg-forest-600/10 grid place-items-center text-forest-700">
                <Users className="h-5 w-5" />
              </div>
              {canJoinSoon(c) && (
                <span className="status-pill status-memorized" data-testid={`live-pill-${c.class_id}`}>
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-forest-700 mr-1 animate-pulse-soft" /> {t("halaqa.soon")}
                </span>
              )}
            </div>
            <h3 className="mt-4 font-display font-semibold text-xl text-forest-700">{c.name}</h3>
            <p className="text-sm text-muted-foreground mt-1">{c.description || "—"}</p>

            <div className="mt-4 text-xs text-muted-foreground space-y-1.5">
              <div className="flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" />
                {c.recurrence?.days_of_week?.length
                  ? `${c.recurrence.days_of_week.join(", ")} · ${c.recurrence.time} ${c.recurrence.tz}`
                  : c.schedule || t("halaqa.flexible")}
              </div>
              <div className="flex items-center gap-1.5 text-forest-700 font-medium">
                <Clock className="h-3.5 w-3.5" />
                {fmtCountdown(c.minutes_until, t)}
              </div>
              <div>{t("halaqa.teacher")} <span className="text-foreground">{c.teacher_name || t("halaqa.tba")}</span></div>
              <div>{t("halaqa.studentsEnrolled", { count: c.student_count || 0 })}</div>
            </div>

            <div className="mt-5 flex items-center gap-2 flex-wrap">
              {!c.is_enrolled ? (
                <button onClick={() => join(c.class_id)} className="btn-primary text-sm" data-testid={`join-${c.class_id}`}>{t("halaqa.joinHalaqa")}</button>
              ) : (
                <>
                  <span className="status-pill status-memorized"><Check className="h-3 w-3 mr-1" /> {t("halaqa.enrolled")}</span>
                  {c.zoom_url && (
                    <button
                      onClick={() => attendAndOpen(c)}
                      disabled={!canJoinSoon(c)}
                      className={`text-sm inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full font-medium transition ${
                        canJoinSoon(c)
                          ? "bg-gold-500 text-foreground hover:bg-gold-600"
                          : "bg-sand-200 text-muted-foreground cursor-not-allowed"
                      }`}
                      data-testid={`join-zoom-${c.class_id}`}
                      title={canJoinSoon(c) ? t("halaqa.joinZoom") : t("halaqa.available30MinBeforeStart")}
                    >
                      <Video className="h-3.5 w-3.5" /> {t("halaqa.joinZoom")}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
